import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { 
  Building2, Users, Briefcase, MessageSquare, Star, Calculator, 
  Settings, ChevronDown, ChevronRight, LogOut, FileText, UserPlus,
  FolderOpen, ClipboardList, LayoutDashboard, Menu, X, Layers, HelpCircle, Phone, BookOpen, Shield, PenTool, CreditCard, DollarSign, Home, Search
} from 'lucide-react';
import { Button } from '../ui/button';
import { companyInfo } from '../../data/mock';

const navItems = [
  {
    label: 'Marketing',
    icon: PenTool,
    children: [
      { label: 'Content Studio', path: '/admin/content', icon: PenTool },
      { label: 'SEO Dashboard', path: '/admin/seo', icon: Search },
    ]
  },
  {
    label: 'Knowledge Base',
    icon: BookOpen,
    children: [
      { label: 'Manage Articles', path: '/admin/knowledge-base', icon: BookOpen },
    ]
  },
  {
    label: 'Content Management',
    icon: FolderOpen,
    children: [
      { label: 'Project Portfolio', path: '/admin/projects', icon: Building2 },
      { label: 'Services', path: '/admin/services', icon: Layers },
      { label: 'House Plans', path: '/admin/plans', icon: Home },
      { label: 'Testimonials', path: '/admin/testimonials', icon: Star },
      { label: 'FAQs', path: '/admin/faq', icon: HelpCircle },
      { label: 'Company Standards', path: '/admin/standards', icon: BookOpen },
      { label: 'Project Estimator', path: '/admin/estimator', icon: Calculator },
    ]
  },
  {
    label: 'Communications',
    icon: Phone,
    children: [
      { label: 'Communications', path: '/admin/phone', icon: Phone },
      { label: 'Contact Submissions', path: '/admin/contact', icon: MessageSquare },
    ]
  },
  {
    label: 'Vendors',
    icon: Briefcase,
    children: [
      { label: 'Vendor Applications', path: '/admin/vendors', icon: Briefcase },
    ]
  },
  {
    label: 'Human Resources',
    icon: Users,
    children: [
      { label: 'Employment Applications', path: '/admin/employment', icon: UserPlus },
      { label: 'Job Postings', path: '/admin/jobs', icon: FileText },
    ]
  },
  {
    label: 'Integrations',
    icon: CreditCard,
    children: [
      { label: 'Bill Pay', path: '/admin/billpay', icon: CreditCard },
    ]
  },
  {
    label: 'Financial Management',
    icon: DollarSign,
    children: [
      { label: 'Cash Flow Manager', path: '/admin/cashflow', icon: DollarSign },
    ]
  },
  {
    label: 'Settings',
    icon: Settings,
    children: [
      { label: 'User Management', path: '/admin/users', icon: Users },
      { label: 'Roles & Permissions', path: '/admin/roles', icon: Shield },
    ]
  }
];

const AdminSidebar = ({ children, pageTitle, pageIcon: PageIcon }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [expandedSections, setExpandedSections] = useState(['Marketing', 'Knowledge Base', 'Content Management', 'Communications', 'Vendors', 'Human Resources', 'Integrations', 'Financial Management', 'Settings']);
  const [isMobileOpen, setIsMobileOpen] = useState(false);

  const toggleSection = (label) => {
    setExpandedSections(prev => 
      prev.includes(label) 
        ? prev.filter(s => s !== label)
        : [...prev, label]
    );
  };

  const isActive = (path) => location.pathname === path;

  const handleLogout = () => {
    localStorage.removeItem('adminLoggedIn');
    localStorage.removeItem('adminUser');
    localStorage.removeItem('admin_user');
    localStorage.removeItem('admin_session_id');
    localStorage.removeItem('adminToken');
    localStorage.removeItem('adminTokenExpires');
    navigate('/admin/login');
  };

  const SidebarContent = () => (
    <div className="flex flex-col h-full">
      {/* Logo */}
      <div className="p-4 border-b border-slate-700">
        <Link to="/" className="flex items-center gap-3">
          <img 
            src={companyInfo.logoIcon} 
            alt="C3 Logo" 
            className="w-9 h-9 object-contain"
          />
          <span className="font-bold text-white text-lg">Control Center</span>
        </Link>
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto p-3">
        {navItems.map((section) => {
          const SectionIcon = section.icon;
          const isExpanded = expandedSections.includes(section.label);
          const hasActiveChild = section.children.some(child => isActive(child.path));

          return (
            <div key={section.label} className="mb-1">
              <button
                onClick={() => toggleSection(section.label)}
                className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                  hasActiveChild 
                    ? 'bg-slate-700 text-white' 
                    : 'text-slate-400 hover:bg-slate-700/50 hover:text-white'
                }`}
              >
                <div className="flex items-center gap-2">
                  <SectionIcon className="w-4 h-4" />
                  <span>{section.label}</span>
                </div>
                {isExpanded 
                  ? <ChevronDown className="w-4 h-4" />
                  : <ChevronRight className="w-4 h-4" />
                }
              </button>
              
              {isExpanded && (
                <div className="mt-1 ml-3 border-l border-slate-700 pl-3 space-y-1">
                  {section.children.map((item) => {
                    const ItemIcon = item.icon;
                    return (
                      <Link
                        key={item.path}
                        to={item.path}
                        onClick={() => setIsMobileOpen(false)}
                        className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors ${
                          isActive(item.path)
                            ? 'bg-cyan-600 text-white'
                            : 'text-slate-400 hover:bg-slate-700/50 hover:text-white'
                        }`}
                      >
                        <ItemIcon className="w-4 h-4" />
                        <span>{item.label}</span>
                      </Link>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </nav>

      {/* Footer */}
      <div className="p-3 border-t border-slate-700">
        <Link 
          to="/"
          className="flex items-center gap-2 px-3 py-2 text-sm text-slate-400 hover:text-white transition-colors"
        >
          <Building2 className="w-4 h-4" />
          <span>Return to Website</span>
        </Link>
        <button
          onClick={handleLogout}
          className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-400 hover:text-red-300 hover:bg-red-900/20 rounded-lg transition-colors"
        >
          <LogOut className="w-4 h-4" />
          <span>Logout</span>
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-100 flex">
      {/* Desktop Sidebar */}
      <aside className="hidden lg:flex lg:flex-col lg:w-64 lg:fixed lg:inset-y-0 bg-slate-800">
        <SidebarContent />
      </aside>

      {/* Mobile Sidebar Overlay */}
      {isMobileOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setIsMobileOpen(false)}
        />
      )}

      {/* Mobile Sidebar */}
      <aside className={`fixed inset-y-0 left-0 w-64 bg-slate-800 z-50 transform transition-transform lg:hidden ${
        isMobileOpen ? 'translate-x-0' : '-translate-x-full'
      }`}>
        <SidebarContent />
      </aside>

      {/* Main Content */}
      <div className="flex-1 min-w-0 lg:ml-64">
        {/* Top Header */}
        <header className="sticky top-0 z-30 bg-white border-b border-slate-200 shadow-sm">
          <div className="flex items-center justify-between h-14 px-4 sm:px-6">
            <div className="flex items-center gap-3">
              {/* Mobile menu button */}
              <button
                onClick={() => setIsMobileOpen(!isMobileOpen)}
                className="lg:hidden p-2 rounded-lg hover:bg-slate-100"
              >
                {isMobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
              </button>
              
              {PageIcon && (
                <div className="hidden sm:flex w-8 h-8 bg-cyan-100 rounded-lg items-center justify-center">
                  <PageIcon className="w-4 h-4 text-cyan-600" />
                </div>
              )}
              <h1 className="text-lg font-bold text-slate-900">{pageTitle}</h1>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="p-4 sm:p-6">
          {children}
        </main>
      </div>
    </div>
  );
};

export default AdminSidebar;
